import { useState, type FormEvent } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { AlertBanner, Button, FormField, Heading, Input, Text } from "@lendcord/concord";
import { PasswordInput } from "@/auth/components/PasswordInput";
import { OtpBoxes, OTP_LENGTH } from "@/auth/components/OtpBoxes";
import {
  useConfirmPasswordResetMutation,
  useRequestPasswordResetMutation,
  type AuthTokenResponse,
  type LoginIdentifierType,
} from "@/auth/api/auth.api";
import { isQueryApiError } from "@/app/store/baseApi";
import { EMAIL_REGEX, PASSWORD_REGEX, PHONE_REGEX, toIndianE164 } from "@/auth/validators";

interface ForgotPasswordFormProps {
  onSuccess: (result: AuthTokenResponse) => void;
  onBack: () => void;
}

type Step = "request" | "reset";

export function ForgotPasswordForm({ onSuccess, onBack }: ForgotPasswordFormProps) {
  const intl = useIntl();
  const [requestPasswordReset] = useRequestPasswordResetMutation();
  const [confirmPasswordReset] = useConfirmPasswordResetMutation();
  const [step, setStep] = useState<Step>("request");
  const [identifierInput, setIdentifierInput] = useState("");
  const [identifierError, setIdentifierError] = useState<string | undefined>();
  const [identifierType, setIdentifierType] = useState<LoginIdentifierType>("EMAIL");
  const [identifier, setIdentifier] = useState("");

  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetErrors, setResetErrors] = useState<{
    otp?: string;
    newPassword?: string;
    confirmPassword?: string;
  }>({});

  const [formError, setFormError] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  function handleStartOver() {
    setStep("request");
    setOtp("");
    setNewPassword("");
    setConfirmPassword("");
    setResetErrors({});
    setFormError(undefined);
  }

  async function handleRequestSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError(undefined);

    const trimmed = identifierInput.trim();
    const isEmail = EMAIL_REGEX.test(trimmed);
    const isPhone = PHONE_REGEX.test(trimmed);
    if (!isEmail && !isPhone) {
      setIdentifierError(intl.formatMessage({ id: "auth.forgotPasswordForm.identifierError" }));
      return;
    }
    setIdentifierError(undefined);

    const type: LoginIdentifierType = isEmail ? "EMAIL" : "PHONE";
    const value = isEmail ? trimmed : toIndianE164(trimmed);

    setIsSubmitting(true);
    try {
      await requestPasswordReset({ identifierType: type, identifier: value }).unwrap();
      setIdentifierType(type);
      setIdentifier(value);
      setStep("reset");
    } catch {
      setFormError(intl.formatMessage({ id: "auth.forgotPasswordForm.genericError" }));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleResetSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError(undefined);

    const nextErrors: typeof resetErrors = {};
    if (otp.length !== OTP_LENGTH) {
      nextErrors.otp = intl.formatMessage({ id: "auth.forgotPasswordForm.requiredCodeError" });
    }
    if (!PASSWORD_REGEX.test(newPassword)) {
      nextErrors.newPassword = intl.formatMessage({ id: "auth.forgotPasswordForm.newPasswordError" });
    }
    if (confirmPassword !== newPassword) {
      nextErrors.confirmPassword = intl.formatMessage({ id: "auth.forgotPasswordForm.confirmPasswordError" });
    }
    setResetErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setIsSubmitting(true);
    try {
      const result = await confirmPasswordReset({ identifierType, identifier, otp, newPassword }).unwrap();
      onSuccess(result);
    } catch (error) {
      if (isQueryApiError(error) && error.code === "LEND-071") {
        setFormError(intl.formatMessage({ id: "auth.forgotPasswordForm.tooManyAttemptsError" }));
      } else if (isQueryApiError(error) && error.code === "LEND-070") {
        setFormError(intl.formatMessage({ id: "auth.forgotPasswordForm.invalidCodeError" }));
      } else {
        setFormError(intl.formatMessage({ id: "auth.forgotPasswordForm.genericError" }));
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  if (step === "request") {
    return (
      <form onSubmit={handleRequestSubmit} noValidate className="flex flex-col gap-5">
        <Heading level={2} className="mb-1">
          <FormattedMessage id="auth.forgotPasswordForm.requestHeading" />
        </Heading>
        <Text size="sm" variant="muted" className="-mt-4">
          <FormattedMessage id="auth.forgotPasswordForm.requestSubtitle" />
        </Text>

        {formError && <AlertBanner status="error">{formError}</AlertBanner>}

        <FormField
          label={intl.formatMessage({ id: "auth.forgotPasswordForm.identifierLabel" })}
          error={identifierError}
          required
        >
          <Input
            value={identifierInput}
            onChange={(event) => setIdentifierInput(event.target.value)}
            placeholder={intl.formatMessage({ id: "auth.forgotPasswordForm.identifierPlaceholder" })}
            autoComplete="username"
          />
        </FormField>

        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <FormattedMessage id="auth.forgotPasswordForm.sendingButton" />
          ) : (
            <FormattedMessage id="auth.forgotPasswordForm.sendCodeButton" />
          )}
        </Button>

        <button
          type="button"
          onClick={onBack}
          className="self-center text-sm font-medium text-accent-600 hover:underline"
        >
          <FormattedMessage id="auth.forgotPasswordForm.backToLoginLink" />
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={handleResetSubmit} noValidate className="flex flex-col gap-5">
      <Heading level={2} className="mb-1">
        <FormattedMessage id="auth.forgotPasswordForm.resetHeading" />
      </Heading>
      <Text size="sm" variant="muted" className="-mt-4">
        <FormattedMessage id="auth.forgotPasswordForm.resetSubtitle" values={{ identifier }} />{" "}
        <button
          type="button"
          onClick={handleStartOver}
          className="font-medium text-accent-600 hover:underline"
        >
          <FormattedMessage id="auth.forgotPasswordForm.startOverLink" />
        </button>
      </Text>

      {formError && <AlertBanner status="error">{formError}</AlertBanner>}

      <FormField
        label={intl.formatMessage({ id: "auth.forgotPasswordForm.codeLabel" })}
        error={resetErrors.otp}
        required
      >
        <OtpBoxes value={otp} onChange={setOtp} />
      </FormField>

      <FormField
        label={intl.formatMessage({ id: "auth.forgotPasswordForm.newPasswordLabel" })}
        error={resetErrors.newPassword}
        required
        hint={
          resetErrors.newPassword
            ? undefined
            : intl.formatMessage({ id: "auth.forgotPasswordForm.newPasswordHint" })
        }
      >
        <PasswordInput
          value={newPassword}
          onChange={setNewPassword}
          placeholder={intl.formatMessage({ id: "auth.forgotPasswordForm.newPasswordPlaceholder" })}
          autoComplete="new-password"
        />
      </FormField>

      <FormField
        label={intl.formatMessage({ id: "auth.forgotPasswordForm.confirmPasswordLabel" })}
        error={resetErrors.confirmPassword}
        required
      >
        <PasswordInput
          value={confirmPassword}
          onChange={setConfirmPassword}
          placeholder={intl.formatMessage({ id: "auth.forgotPasswordForm.confirmPasswordPlaceholder" })}
          autoComplete="new-password"
          disablePaste
        />
      </FormField>

      <Button type="submit" disabled={isSubmitting}>
        {isSubmitting ? (
          <FormattedMessage id="auth.forgotPasswordForm.resettingButton" />
        ) : (
          <FormattedMessage id="auth.forgotPasswordForm.resetPasswordButton" />
        )}
      </Button>
    </form>
  );
}
